import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../../lib/api";
import { MonthCalendar, Legend } from "./MonthCalendar";
import { QuickEntry } from "./QuickEntry";
import { EntriesList } from "./EntriesList";
import { UsualHours } from "./UsualHours";
import { ConsultantEmails } from "./ConsultantEmails";
import { addDays, monthGrid, subtractDays, ymd } from "./rules";

let _seq = 0;
const withKey = (x) => ({ ...x, _k: x._k || `row-${++_seq}` });
const stripKeys = (xs) => xs.map(({ _k, ...x }) => x); // eslint-disable-line no-unused-vars
const londonDay = (iso) => ymd(new Date(new Date(iso).toLocaleString("en-US", { timeZone: "Europe/London" })));

const useMonthData = (slug, month, version) => {
  const [bookedByDay, setBooked] = useState({});
  const [freeByDay, setFree] = useState(null);
  useEffect(() => {
    const grid = monthGrid(`${month}-01`);
    const weeks = Array.from({ length: grid.length / 7 }, (_, i) => grid[i * 7]);
    let live = true;
    setFree(null);
    api.get("/admin/calendar", { params: { start: grid[0], days: grid.length } }).then(({ data }) => {
      const m = {};
      data.bookings.filter((b) => b.consultant_slug === slug).forEach((b) => { const d = londonDay(b.start_time); m[d] = (m[d] || 0) + 1; });
      if (live) setBooked(m);
    }).catch(() => {});
    Promise.all(weeks.map((w) => api.get("/booking/slots", { params: { consultant: slug, start: w } }).then((r) => r.data.slots).catch(() => [])))
      .then((lists) => {
        const m = {};
        grid.forEach((d) => { m[d] = 0; });
        lists.flat().forEach((iso) => { const d = londonDay(iso); m[d] = (m[d] || 0) + 1; });
        if (live) setFree(m);
      });
    return () => { live = false; };
  }, [slug, month, version]);
  return { bookedByDay, freeByDay };
};

const SaveBar = ({ dirty, busy, onSave, slug }) => (
  <div className={`sticky bottom-4 z-10 flex items-center justify-between gap-4 p-4 rounded-md border shadow-sm ${dirty ? "bg-white border-bronze-dark" : "bg-white/80 border-stone-200"}`}>
    <span data-testid="availability-dirty" className="text-sm">{dirty ? "You have unsaved changes - save to update the free times shown on the calendar." : "All changes saved."}</span>
    <button type="button" onClick={onSave} disabled={busy || !dirty} data-testid={`availability-save-${slug}`} className="btn-primary disabled:opacity-50">
      {busy ? "Saving…" : "Save changes"}
    </button>
  </div>
);

export const AvailabilityPlanner = ({ rule, minNoticeDays, onSaved }) => {
  const today = ymd(new Date());
  const [r, setR] = useState(() => ({ ...rule, blocked: rule.blocked.map(withKey), open_dates: rule.open_dates.map(withKey) }));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [picked, setPicked] = useState(null);
  const [version, setVersion] = useState(0);
  const { bookedByDay, freeByDay } = useMonthData(r.slug, month, version);
  const minDay = useMemo(() => addDays(today, minNoticeDays), [today, minNoticeDays]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const change = useCallback((fn) => { setR(fn); setDirty(true); }, []);
  const set = (k, v) => change((x) => ({ ...x, [k]: v }));

  const addEntry = (kind, { from, to, days, start, until }) => change((x) => {
    const others = x.open_dates.filter((o) => !days.includes(o.date));
    if (kind === "closed") return { ...x, blocked: [...x.blocked, withKey({ from, to })], open_dates: others };
    const added = days.map((d) => withKey({ date: d, from: start, until }));
    return { ...x, blocked: subtractDays(x.blocked, days).map(withKey), open_dates: [...others, ...added] };
  });

  const save = async () => {
    setBusy(true);
    try {
      const { slug, name, updated_at, updated_by, ...body } = r; // eslint-disable-line no-unused-vars
      await api.put(`/admin/availability/${slug}`, { ...body, blocked: stripKeys(body.blocked), open_dates: stripKeys(body.open_dates) });
      toast.success(`Saved availability for ${name}`);
      setDirty(false);
      setVersion((v) => v + 1);
      onSaved?.(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`availability-card-${r.slug}`} className="space-y-6 pb-4">
      <div className="grid xl:grid-cols-[1fr_380px] gap-6 items-start">
        <div className="p-5 bg-white border border-stone-200/60 rounded-md space-y-4">
          <Legend />
          <MonthCalendar rule={r} month={month} setMonth={setMonth} today={today} minDay={minDay} freeByDay={dirty ? null : freeByDay}
            bookedByDay={bookedByDay} selected={picked} onPick={setPicked} />
          {dirty && <p className="text-xs text-ink-muted">Free-time counts show the saved schedule - they update after you save.</p>}
        </div>
        <div className="space-y-6">
          <QuickEntry pickedDay={picked} onAdd={addEntry} />
          <EntriesList rule={r} today={today}
            onRemoveBlocked={(i) => change((x) => ({ ...x, blocked: x.blocked.filter((_, j) => j !== i) }))}
            onRemoveOpen={(i) => change((x) => ({ ...x, open_dates: x.open_dates.filter((_, j) => j !== i) }))} />
        </div>
      </div>
      <ConsultantEmails slug={r.slug} name={r.name} />
      <UsualHours rule={r} set={set} />
      <SaveBar dirty={dirty} busy={busy} onSave={save} slug={r.slug} />
    </div>
  );
};
